import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TemplateLibrary = ({ onSelectTemplate, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock template data
  const templates = [
    {
      id: 1,
      name: 'Morgenschicht Kundenservice',
      category: 'customer-service',
      department: 'Kundenservice',
      startTime: '08:00',
      endTime: '16:00',
      maxEmployees: 3,
      requiredSkills: ['customer-support'],
      payGrade: 'standard',
      usageCount: 45,
      lastUsed: '2025-09-28',
      description: 'Standard Morgenschicht für Kundenservice mit 3 Mitarbeitern'
    },
    {
      id: 2,
      name: 'Nachtschicht Sicherheit',
      category: 'security',
      department: 'Sicherheit',
      startTime: '22:00',
      endTime: '06:00',
      maxEmployees: 2,
      requiredSkills: ['safety-certified'],
      payGrade: 'senior',
      usageCount: 32,
      lastUsed: '2025-09-30',
      description: 'Nachtschicht für Sicherheitspersonal mit Zertifizierung'
    },
    {
      id: 3,
      name: 'Produktionsschicht',
      category: 'manufacturing',
      department: 'Fertigung',
      startTime: '06:00',
      endTime: '14:00',
      maxEmployees: 8,
      requiredSkills: ['quality-control', 'technical-skills'],
      payGrade: 'standard',
      usageCount: 67,
      lastUsed: '2025-10-01',
      description: 'Frühe Produktionsschicht mit Qualitätskontrolle'
    },
    {
      id: 4,
      name: 'Wochenendschicht',
      category: 'operations',
      department: 'Betrieb',
      startTime: '10:00',
      endTime: '18:00',
      maxEmployees: 4,
      requiredSkills: ['leadership'],
      payGrade: 'lead',
      usageCount: 23,
      lastUsed: '2025-09-29',
      description: 'Wochenendschicht mit Führungsverantwortung'
    },
    {
      id: 5,
      name: 'Verwaltung Teilzeit',
      category: 'administration',
      department: 'Verwaltung',
      startTime: '09:00',
      endTime: '13:00',
      maxEmployees: 2,
      requiredSkills: ['data-entry'],
      payGrade: 'entry',
      usageCount: 18,
      lastUsed: '2025-09-27',
      description: 'Teilzeitschicht für administrative Aufgaben'
    }
  ];

  const categories = [
    { value: 'all', label: 'Alle Kategorien', icon: 'Grid3X3' },
    { value: 'customer-service', label: 'Kundenservice', icon: 'Headphones' },
    { value: 'operations', label: 'Betrieb', icon: 'Settings' },
    { value: 'manufacturing', label: 'Fertigung', icon: 'Cog' },
    { value: 'administration', label: 'Verwaltung', icon: 'FileText' },
    { value: 'security', label: 'Sicherheit', icon: 'Shield' }
  ];

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template?.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleSelectTemplate = (template) => {
    if (onSelectTemplate) {
      onSelectTemplate({
        title: template?.name,
        department: template?.category,
        startTime: template?.startTime,
        endTime: template?.endTime,
        maxEmployees: template?.maxEmployees,
        requiredSkills: template?.requiredSkills,
        payGrade: template?.payGrade,
        specialInstructions: template?.description
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Vorlagenbibliothek</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Wählen Sie eine Vorlage aus, um schnell eine neue Schicht zu erstellen
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-border">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Vorlagen durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories?.map((category) => (
                <Button
                  key={category?.value}
                  variant={selectedCategory === category?.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category?.value)}
                  iconName={category?.icon}
                  iconPosition="left"
                  iconSize={14}
                >
                  {category?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Templates Grid */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {filteredTemplates?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Keine Vorlagen gefunden</p>
              <p className="text-sm text-muted-foreground mt-1">
                Versuchen Sie andere Suchbegriffe oder Kategorien
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredTemplates?.map((template) => (
                <div
                  key={template?.id}
                  className="border border-border rounded-lg p-4 hover:shadow-card transition-shadow cursor-pointer"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-foreground">{template?.name}</h3>
                      <p className="text-sm text-muted-foreground">{template?.department}</p>
                    </div>
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                      <Icon name="Clock" size={12} />
                      <span>{template?.usageCount}x verwendet</span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {template?.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Zeit</p>
                      <p className="text-sm font-medium text-foreground">
                        {template?.startTime} - {template?.endTime}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Mitarbeiter</p>
                      <p className="text-sm font-medium text-foreground">
                        {template?.maxEmployees} Person(en)
                      </p>
                    </div>
                  </div>

                  {template?.requiredSkills?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground mb-2">Fähigkeiten</p>
                      <div className="flex flex-wrap gap-1">
                        {template?.requiredSkills?.slice(0, 3)?.map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-md"
                          >
                            {skill}
                          </span>
                        ))}
                        {template?.requiredSkills?.length > 3 && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                            +{template?.requiredSkills?.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Zuletzt verwendet: {new Date(template.lastUsed)?.toLocaleDateString('de-DE')}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      iconSize={14}
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleSelectTemplate(template);
                      }}
                    >
                      Verwenden
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredTemplates?.length} von {templates?.length} Vorlagen angezeigt
            </p>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Schließen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;